import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import {
  Box, Typography, Button, Paper, Table, TableBody, TableCell,
  TableContainer, TableHead, TableRow, Dialog, DialogTitle,
  DialogContent, DialogActions, TextField, MenuItem, Chip,
  IconButton, Tooltip, Alert, CircularProgress, Autocomplete, Grid, Divider
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import EditIcon from '@mui/icons-material/Edit';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import DeleteIcon from '@mui/icons-material/Delete';
import SaveIcon from '@mui/icons-material/Save';
import CloseIcon from '@mui/icons-material/Close';

const API = '/api/contabilidad';

const BILLETES = [
  { col: 'det_200', label: 'S/ 200', valor: 200 },
  { col: 'det_100', label: 'S/ 100', valor: 100 },
  { col: 'det_50',  label: 'S/ 50',  valor: 50 },
  { col: 'det_20',  label: 'S/ 20',  valor: 20 },
  { col: 'det_10',  label: 'S/ 10',  valor: 10 },
  { col: 'det_5',   label: 'S/ 5',   valor: 5 },
  { col: 'det_2',   label: 'S/ 2',   valor: 2 },
  { col: 'det_1',   label: 'S/ 1',   valor: 1 },
  { col: 'det_050', label: 'S/ 0.50', valor: 0.50 },
  { col: 'det_020', label: 'S/ 0.20', valor: 0.20 },
  { col: 'det_010', label: 'S/ 0.10', valor: 0.10 },
  { col: 'det_005', label: 'S/ 0.05', valor: 0.05 },
];

const EMPTY_DET = { det_200: 0, det_100: 0, det_50: 0, det_20: 0, det_10: 0, det_5: 0, det_2: 0, det_1: 0, det_050: 0, det_020: 0, det_010: 0, det_005: 0 };

function fmt(v) { return parseFloat(v || 0).toLocaleString('es-PE', { minimumFractionDigits: 2, maximumFractionDigits: 2 }); }

function calcEfectivo(det) {
  return BILLETES.reduce((acc, b) => acc + b.valor * (parseInt(det[b.col]) || 0), 0);
}

function chipEstado(estado) {
  const map = { BORRADOR: 'default', APROBADO: 'success', AJUSTADO: 'info' };
  return <Chip label={estado} color={map[estado] || 'default'} size="small" />;
}

export default function ArqueoCaja() {
  const [empresas, setEmpresas] = useState([]);
  const [empresaId, setEmpresaId] = useState('');
  const [periodos, setPeriodos] = useState([]);
  const [periodoSel, setPeriodoSel] = useState('');
  const [arqueos, setArqueos] = useState([]);
  const [loading, setLoading] = useState(false);
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [cuentasCaja, setCuentasCaja] = useState([]);
  const [cuentaGasto, setCuentaGasto] = useState([]);
  const [form, setForm] = useState({
    cuenta_id: '', fecha_arqueo: '', saldo_libros: '', observacion: '', ...EMPTY_DET
  });
  const [efectivoCalc, setEfectivoCalc] = useState(0);
  const [saveErr, setSaveErr] = useState('');
  const [snack, setSnack] = useState('');
  // Aprobación
  const [aprobarDialog, setAprobarDialog] = useState(false);
  const [aprobarArqueo, setAprobarArqueo] = useState(null);
  const [cuentaDifId, setCuentaDifId] = useState('');
  const [generarAsiento, setGenerarAsiento] = useState(true);

  useEffect(() => {
    axios.get(`${API}/empresas`).then(r => setEmpresas(r.data)).catch(() => {});
  }, []);

  useEffect(() => {
    if (!empresaId) return;
    axios.get(`${API}/periodos?empresa_id=${empresaId}`).then(r => {
      const abiertos = r.data.filter(p => p.estado === 'ABIERTO').sort((a, b) => b.periodo.localeCompare(a.periodo));
      setPeriodos(r.data);
      if (abiertos.length > 0 && !periodoSel) setPeriodoSel(abiertos[0].periodo);
    }).catch(() => {});
    axios.get(`${API}/plan?empresa_id=${empresaId}&solo_hojas=1&buscar=10`).then(r => setCuentasCaja(r.data)).catch(() => {});
    axios.get(`${API}/plan?empresa_id=${empresaId}&solo_hojas=1`).then(r =>
      setCuentaGasto(r.data.filter(c => c.codigo?.startsWith('6') || c.codigo?.startsWith('7')))
    ).catch(() => {});
  }, [empresaId]);

  const fetchArqueos = useCallback(async () => {
    if (!empresaId) return;
    setLoading(true);
    try {
      const r = await axios.get(`${API}/arqueo-caja`, { params: { empresa_id: empresaId, periodo: periodoSel || undefined } });
      setArqueos(r.data);
    } catch (e) { console.error(e); }
    setLoading(false);
  }, [empresaId, periodoSel]);

  useEffect(() => { fetchArqueos(); }, [fetchArqueos]);

  function handleDetChange(col, value) {
    const upd = { ...form, [col]: parseInt(value) || 0 };
    setForm(upd);
    setEfectivoCalc(calcEfectivo(upd));
  }

  function handleFieldChange(e) {
    setForm({ ...form, [e.target.name]: e.target.value });
  }

  function openNew() {
    const detInit = { ...EMPTY_DET };
    setForm({ cuenta_id: '', fecha_arqueo: new Date().toLocaleDateString('en-CA'), saldo_libros: '', observacion: '', ...detInit });
    setEfectivoCalc(0);
    setEditing(null);
    setSaveErr('');
    setOpen(true);
  }

  function openEdit(a) {
    const det = {};
    BILLETES.forEach(b => { det[b.col] = a[b.col] || 0; });
    setForm({ cuenta_id: a.cuenta_id, fecha_arqueo: a.fecha_arqueo?.substring(0, 10), saldo_libros: a.saldo_libros, observacion: a.observacion || '', ...det });
    setEfectivoCalc(parseFloat(a.efectivo_contado) || 0);
    setEditing(a.id);
    setSaveErr('');
    setOpen(true);
  }

  async function handleSave() {
    if (!form.cuenta_id || !form.fecha_arqueo || !periodoSel) {
      setSaveErr('Cuenta, fecha y período son requeridos');
      return;
    }
    setSaveErr('');
    try {
      const payload = { ...form, empresa_id: empresaId, periodo: periodoSel, saldo_libros: parseFloat(form.saldo_libros) || 0 };
      if (editing) {
        await axios.put(`${API}/arqueo-caja/${editing}`, payload);
      } else {
        await axios.post(`${API}/arqueo-caja`, payload);
      }
      setOpen(false);
      setSnack(editing ? 'Arqueo actualizado' : 'Arqueo guardado');
      fetchArqueos();
    } catch (e) {
      setSaveErr(e.response?.data?.error || 'Error al guardar');
    }
  }

  async function handleEliminar(id) {
    if (!window.confirm('¿Eliminar este arqueo?')) return;
    try {
      await axios.delete(`${API}/arqueo-caja/${id}`);
      setSnack('Arqueo eliminado');
      fetchArqueos();
    } catch (e) { alert(e.response?.data?.error || 'Error'); }
  }

  async function handleAprobar() {
    try {
      const r = await axios.put(`${API}/arqueo-caja/${aprobarArqueo.id}/aprobar`, {
        cuenta_diferencia_id: cuentaDifId || null,
        generar_asiento: generarAsiento,
      });
      setAprobarDialog(false);
      setSnack(`Arqueo ${r.data.estado}`);
      fetchArqueos();
    } catch (e) { alert(e.response?.data?.error || 'Error'); }
  }

  const diferencia = parseFloat((efectivoCalc - (parseFloat(form.saldo_libros) || 0)).toFixed(2));

  return (
    <Box sx={{ width: '100%', maxWidth: 1400, mx: 'auto', mt: 1 }}>
      <Typography variant="h5" fontWeight="bold" mb={2}>Arqueo de Caja</Typography>

      <Paper sx={{ p: 2, mb: 2, borderRadius: 3 }}>
        <Grid container spacing={2} alignItems="center">
          <Grid item xs={12} sm="auto">
            <TextField select fullWidth size="small" label="Empresa" value={empresaId} onChange={e => { setEmpresaId(e.target.value); setPeriodoSel(''); }} sx={{ minWidth: '24em' }}>
              {empresas.map(e => <MenuItem key={e.id} value={e.id}>{e.razon_social}</MenuItem>)}
            </TextField>
          </Grid>
          <Grid item xs={12} sm="auto">
            <TextField select fullWidth size="small" label="Período" value={periodoSel} onChange={e => setPeriodoSel(e.target.value)} disabled={!empresaId} sx={{ minWidth: '20em' }}>
              <MenuItem value="">Todos</MenuItem>
              {periodos.map(p => <MenuItem key={p.periodo} value={p.periodo}>{p.nombre || p.periodo}</MenuItem>)}
            </TextField>
          </Grid>
          <Grid item xs={12} sm={5} md={7} sx={{ display: 'flex', justifyContent: 'flex-end' }}>
            <Button variant="contained" startIcon={<AddIcon />} disabled={!empresaId || !periodoSel} onClick={openNew}>Nuevo Arqueo</Button>
          </Grid>
        </Grid>
      </Paper>

      {loading ? <CircularProgress sx={{ display: 'block', mx: 'auto', mt: 4 }} /> : (
        <TableContainer component={Paper} sx={{ borderRadius: 3, boxShadow: '0 2px 12px rgba(0,0,0,0.08)', overflowX: 'auto' }}>
          <Table size="small">
            <TableHead sx={{ bgcolor: '#f5f5f5' }}>
              <TableRow>
                <TableCell>Fecha</TableCell>
                <TableCell>Cuenta Caja</TableCell>
                <TableCell align="right">Saldo Libros</TableCell>
                <TableCell align="right">Efectivo Contado</TableCell>
                <TableCell align="right">Diferencia</TableCell>
                <TableCell>Estado</TableCell>
                <TableCell>Acciones</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {arqueos.map(a => (
                <TableRow key={a.id} hover>
                  <TableCell>{a.fecha_arqueo?.substring(0, 10)}</TableCell>
                  <TableCell>{a.cuenta_codigo} — {a.cuenta_nombre}</TableCell>
                  <TableCell align="right">S/ {fmt(a.saldo_libros)}</TableCell>
                  <TableCell align="right">S/ {fmt(a.efectivo_contado)}</TableCell>
                  <TableCell align="right" sx={{ color: parseFloat(a.diferencia) > 0 ? 'success.main' : parseFloat(a.diferencia) < 0 ? 'error.main' : 'text.primary', fontWeight: 'bold' }}>
                    {parseFloat(a.diferencia) > 0 ? '+' : ''}{fmt(a.diferencia)}
                  </TableCell>
                  <TableCell>{chipEstado(a.estado)}</TableCell>
                  <TableCell>
                    {a.estado === 'BORRADOR' && (
                      <>
                        <Tooltip title="Editar"><IconButton size="small" color="primary" onClick={() => openEdit(a)}><EditIcon fontSize="small" /></IconButton></Tooltip>
                        <Tooltip title="Aprobar"><IconButton size="small" color="success" onClick={() => { setAprobarArqueo(a); setCuentaDifId(''); setGenerarAsiento(Math.abs(parseFloat(a.diferencia)) >= 0.01); setAprobarDialog(true); }}><CheckCircleIcon fontSize="small" /></IconButton></Tooltip>
                        <Tooltip title="Eliminar"><IconButton size="small" color="error" onClick={() => handleEliminar(a.id)}><DeleteIcon fontSize="small" /></IconButton></Tooltip>
                      </>
                    )}
                  </TableCell>
                </TableRow>
              ))}
              {arqueos.length === 0 && (
                <TableRow><TableCell colSpan={7} align="center" sx={{ py: 4, color: 'text.secondary' }}>No hay arqueos registrados</TableCell></TableRow>
              )}
            </TableBody>
          </Table>
        </TableContainer>
      )}

      {snack && <Alert severity="success" onClose={() => setSnack('')} sx={{ mt: 2 }}>{snack}</Alert>}

      {/* ── Dialog Nuevo/Editar Arqueo ── */}
      <Dialog open={open} onClose={() => setOpen(false)} maxWidth="md" fullWidth>
        <DialogTitle><Typography fontWeight="bold">{editing ? 'Editar Arqueo' : 'Nuevo Arqueo de Caja'}</Typography></DialogTitle>
        <DialogContent>
          {saveErr && <Alert severity="error" sx={{ mb: 1 }}>{saveErr}</Alert>}
          <Grid container spacing={2} sx={{ mt: 0.5 }}>
            <Grid item xs={12} sm={5}>
              <Autocomplete
                options={cuentasCaja}
                getOptionLabel={o => `${o.codigo} — ${o.descripcion}`}
                value={cuentasCaja.find(c => c.id === form.cuenta_id) || null}
                onChange={(_, v) => setForm({ ...form, cuenta_id: v?.id || '' })}
                renderInput={params => <TextField {...params} size="small" label="Cuenta de Caja" />}
                filterOptions={(opts, { inputValue }) => opts.filter(o =>
                  o.codigo?.includes(inputValue) || o.descripcion?.toLowerCase().includes(inputValue.toLowerCase())
                )}
              />
            </Grid>
            <Grid item xs={6} sm={3}>
              <TextField fullWidth size="small" label="Fecha Arqueo" name="fecha_arqueo" type="date" value={form.fecha_arqueo} onChange={handleFieldChange} InputLabelProps={{ shrink: true }} />
            </Grid>
            <Grid item xs={6} sm={4}>
              <TextField fullWidth size="small" label="Saldo según libros (S/)" name="saldo_libros" type="number" value={form.saldo_libros} onChange={handleFieldChange} inputProps={{ step: '0.01' }} />
            </Grid>

            <Grid item xs={12}><Divider><Typography variant="caption" color="text.secondary">CONTEO DE DENOMINACIONES</Typography></Divider></Grid>

            {BILLETES.map(b => (
              <Grid item xs={6} sm={3} key={b.col}>
                <TextField
                  fullWidth size="small" label={b.label} type="number"
                  value={form[b.col]}
                  onChange={e => handleDetChange(b.col, e.target.value)}
                  inputProps={{ min: 0 }}
                  InputProps={{ endAdornment: <Typography variant="caption" color="text.secondary" sx={{ whiteSpace: 'nowrap', ml: 0.5 }}>= S/{fmt(b.valor * (parseInt(form[b.col]) || 0))}</Typography> }}
                />
              </Grid>
            ))}

            <Grid item xs={12}>
              <Paper sx={{ p: 2, bgcolor: Math.abs(diferencia) < 0.01 ? '#e8f5e9' : diferencia > 0 ? '#e3f2fd' : '#fff3e0', borderRadius: 2 }}>
                <Grid container spacing={2}>
                  <Grid item xs={4}>
                    <Typography variant="caption" color="text.secondary">Saldo libros</Typography>
                    <Typography fontWeight="bold">S/ {fmt(form.saldo_libros)}</Typography>
                  </Grid>
                  <Grid item xs={4}>
                    <Typography variant="caption" color="text.secondary">Efectivo contado</Typography>
                    <Typography fontWeight="bold" color="primary.main">S/ {fmt(efectivoCalc)}</Typography>
                  </Grid>
                  <Grid item xs={4}>
                    <Typography variant="caption" color="text.secondary">Diferencia</Typography>
                    <Typography fontWeight="bold" color={Math.abs(diferencia) < 0.01 ? 'success.main' : diferencia > 0 ? 'info.main' : 'error.main'}>
                      {diferencia > 0 ? 'Sobrante: +' : diferencia < 0 ? 'Faltante: ' : ''} S/ {fmt(Math.abs(diferencia))}
                    </Typography>
                  </Grid>
                </Grid>
              </Paper>
            </Grid>

            <Grid item xs={12}>
              <TextField fullWidth size="small" label="Observación" name="observacion" value={form.observacion} onChange={handleFieldChange} multiline rows={2} />
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button onClick={() => setOpen(false)} startIcon={<CloseIcon />} color="error" variant="outlined">Cancelar</Button>
          <Button onClick={handleSave} startIcon={<SaveIcon />} variant="contained">Guardar Arqueo</Button>
        </DialogActions>
      </Dialog>

      {/* ── Dialog Aprobación ── */}
      <Dialog open={aprobarDialog} onClose={() => setAprobarDialog(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Aprobar Arqueo</DialogTitle>
        <DialogContent>
          {aprobarArqueo && (
            <Box>
              <Typography variant="body2" mb={2}>
                Diferencia detectada: <strong style={{ color: parseFloat(aprobarArqueo.diferencia) > 0 ? 'green' : parseFloat(aprobarArqueo.diferencia) < 0 ? 'red' : 'inherit' }}>
                  {parseFloat(aprobarArqueo.diferencia) > 0 ? 'Sobrante +' : 'Faltante '} S/ {fmt(Math.abs(aprobarArqueo.diferencia))}
                </strong>
              </Typography>
              {Math.abs(parseFloat(aprobarArqueo.diferencia)) >= 0.01 && (
                <Box sx={{ mb: 2 }}>
                  <Typography variant="body2" mb={1}>Selecciona la cuenta de ajuste (sobrante → ingreso 76x / faltante → gasto 66x):</Typography>
                  <Autocomplete
                    options={cuentaGasto}
                    getOptionLabel={o => `${o.codigo} — ${o.descripcion}`}
                    onChange={(_, v) => setCuentaDifId(v?.id || '')}
                    renderInput={params => <TextField {...params} size="small" label="Cuenta de diferencia" />}
                    filterOptions={(opts, { inputValue }) => opts.filter(o =>
                      o.codigo?.startsWith('6') || o.codigo?.startsWith('7') ||
                      o.descripcion?.toLowerCase().includes(inputValue.toLowerCase())
                    )}
                  />
                  <Typography variant="caption" color="text.secondary" mt={0.5} display="block">
                    Si no seleccionas cuenta, el arqueo se aprobará sin asiento de ajuste.
                  </Typography>
                </Box>
              )}
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setAprobarDialog(false)}>Cancelar</Button>
          <Button variant="contained" color="success" startIcon={<CheckCircleIcon />} onClick={handleAprobar}>Aprobar arqueo</Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}

